import React, { useRef, useState } from "react";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_SIZE = 20 * 1024 * 1024; // 20MB

export const IMAGE_UPLOAD_ACCEPT = "image/jpeg,image/jpg,image/png";

export const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Please upload a valid image file (JPG, JPEG, or PNG)";
  }

  if (file.size > MAX_SIZE) {
    return "Image size must be less than 20MB";
  }

  return null;
};

type ImageResult = { success: boolean; error?: string };

interface UseImageUploadOptions {
  /** Uploads the chosen file. Pass null while the owning record is not loaded. */
  upload: ((file: File) => Promise<ImageResult>) | null;
  /** Deletes the current image. Pass null when there is no image to delete. */
  remove: (() => Promise<ImageResult>) | null;
  /** Gates the delete button; the confirm step is gated by `remove`. */
  hasImage: boolean;
  onUploaded?: () => void | Promise<void>;
  onDeleted?: () => void | Promise<void>;
  uploadErrorMessage?: string;
  deleteErrorMessage?: string;
}

/**
 * The hidden-file-input upload/delete block shared by the user and recipe
 * detail pages: validation, the two busy flags, the error, the delete-confirm
 * modal state, and resetting the input.
 */
export const useImageUpload = ({
  upload,
  remove,
  hasImage,
  onUploaded,
  onDeleted,
  uploadErrorMessage = "Failed to upload image",
  deleteErrorMessage = "Failed to delete image",
}: UseImageUploadOptions) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !upload) return;

    setError(null);
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    try {
      const result = await upload(file);
      if (result.success) {
        await onUploaded?.();
      } else {
        setError(result.error || uploadErrorMessage);
      }
    } catch (err: any) {
      setError(err.message || uploadErrorMessage);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openDeleteModal = () => {
    if (hasImage) {
      setShowDeleteModal(true);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!remove) return;

    setDeleting(true);
    setError(null);
    try {
      const result = await remove();
      if (result.success) {
        setShowDeleteModal(false);
        await onDeleted?.();
      } else {
        setError(result.error || deleteErrorMessage);
      }
    } catch (err: any) {
      setError(err.message || deleteErrorMessage);
    } finally {
      setDeleting(false);
    }
  };

  return {
    fileInputRef,
    accept: IMAGE_UPLOAD_ACCEPT,
    uploading,
    deleting,
    error,
    clearError: () => setError(null),
    showDeleteModal,
    triggerUpload,
    handleFileChange,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  };
};

export type ImageUpload = ReturnType<typeof useImageUpload>;
